import React, { ReactNode, useRef } from 'react'
import { Animated, StyleSheet, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { RectButton, Swipeable as SwipeableDefault } from 'react-native-gesture-handler'
import { Theme } from '@theme'
import { Icon, IconName, Text } from '../atoms'

export type ActionSwipeable = {
  icon: IconName
  title: string
  color: string
  onPress: () => void
}

type SwipeableProps = {
  children: ReactNode
  actions: ActionSwipeable[]
}

const ActionSwipeableHeight = 80

export const Swipeable = ({ children, actions }: SwipeableProps): React.ReactElement => {
  const theme = useTheme()
  const styles = getStyle(theme)

  const swipeableRef = useRef<SwipeableDefault>(null)

  const onClose = () => {
    swipeableRef?.current?.close()
  }

  const renderRightAction = (
    icon: IconName,
    text: string,
    onPress: () => void,
    color: string,
    x: number,
    progress: Animated.AnimatedInterpolation<number>,
  ) => {
    const trans = progress.interpolate({
      inputRange: [0, 1],
      outputRange: [x, 0],
    })

    return (
      <Animated.View
        key={text}
        style={[styles.actionContainer, { transform: [{ translateX: trans }] }]}
      >
        <RectButton
          style={[
            styles.actionButton,
            {
              backgroundColor: color,
            },
          ]}
          onPress={() => {
            onPress()
            onClose()
          }}
        >
          <Icon name={icon} color={theme.colors.white} />
          <Text type="bodySmall" style={styles.title}>
            {text}
          </Text>
        </RectButton>
      </Animated.View>
    )
  }

  const renderRightActions = (progress: Animated.AnimatedInterpolation<number>) => (
    <View style={styles.actionsContainer}>
      {actions.map((action, index) =>
        renderRightAction(
          action.icon,
          action.title,
          action.onPress,
          action.color,
          ActionSwipeableHeight * (actions.length - index),
          progress,
        ),
      )}
    </View>
  )

  if (actions.length === 0) {
    return <>{children}</>
  }

  return (
    <SwipeableDefault
      ref={swipeableRef}
      containerStyle={styles.container}
      friction={2}
      enableTrackpadTwoFingerGesture
      rightThreshold={40}
      renderRightActions={renderRightActions}
    >
      {children}
    </SwipeableDefault>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      width: '100%',
    },
    actionsContainer: {
      width: ActionSwipeableHeight * 3,
      flexDirection: 'row',
    },
    actionContainer: {
      flex: 1,
    },
    actionButton: {
      alignItems: 'center',
      flex: 1,
      justifyContent: 'center',
    },
    title: {
      paddingTop: theme.spacing.xs,
      color: theme.colors.white,
    },
  })
